/**
 * TableRow Component
 *
 * Renders a single portfolio table row using column definitions.
 * Eliminates ~400 lines of duplicated row rendering logic from DashboardDemo.
 *
 * Key pattern: Iterates columns dynamically using accessor + formatter
 * instead of hardcoded switch statements.
 *
 * Phase 2 additions:
 * - Editable stage dropdown with locking rules
 * - Double-click editable trade quantity
 * - Row state indicators (dirty, locked, drift)
 */

import { AlertTriangle, Check, FileText, Loader2, RefreshCw, X } from "lucide-react";
import React, { ReactNode, useCallback, useState } from "react";
import TradeActionBadge from "./TradeActionBadge";
import { ColumnDefinition, PortfolioRow } from "./columns/columnTypes";
import {
  getPnlColorClass,
  getPositionColorClass,
  isUsdColumn,
  isUsdValueUnavailable,
  USD_NULL_DISPLAY,
} from "./columns/formatters";

/**
 * Stage option for dropdown
 */
export interface StageOption {
  id: number;
  name: string;
}

/**
 * Order statuses that lock the row (prevent editing) - lowercase for case-insensitive comparison
 */
const LOCKED_ORDER_STATUSES = ["submitted", "presubmitted", "partialfill", "filled", "error"];

/**
 * Stage states that lock the stage dropdown (prevent reassignment)
 * Per Phase 2: Only 'created' and null/undefined (draft) are editable
 */
const LOCKED_STAGE_STATES = ["ready", "executing", "completed", "failed"];

/**
 * Check if a row is locked based on order status (case-insensitive)
 */
function isRowLocked(row: PortfolioRow): boolean {
  return row.orderStatus !== null && LOCKED_ORDER_STATUSES.includes(row.orderStatus.toLowerCase());
}

/**
 * Check if stage dropdown is locked based on stage state
 * Per Phase 2: Stage dropdown is editable only for Created/Draft stages
 */
function isStageDropdownLocked(row: PortfolioRow): boolean {
  // If there's an order in progress, always locked
  if (isRowLocked(row)) return true;
  // If stage is in a locked state (ready, executing, completed, failed), locked
  if (row.stageState && LOCKED_STAGE_STATES.includes(row.stageState)) return true;
  return false;
}

/**
 * Check if trade qty is zero (HOLD - no stage needed)
 */
function isHoldTrade(row: PortfolioRow): boolean {
  return row.tradeQty === 0 || row.tradeQty === null;
}

/**
 * Get styling for order status badge based on status value
 */
function getOrderStatusColor(status: string, isDarkMode: boolean): string {
  const statusLower = status.toLowerCase();
  switch (statusLower) {
    case "filled":
      return isDarkMode ? "bg-green-900/50 text-green-300" : "bg-green-100 text-green-700";
    case "partialfill":
      return isDarkMode ? "bg-yellow-900/50 text-yellow-300" : "bg-yellow-100 text-yellow-700";
    case "submitted":
    case "presubmitted":
      return isDarkMode ? "bg-blue-900/50 text-blue-300" : "bg-blue-100 text-blue-700";
    case "cancelled":
      return isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600";
    case "error":
    case "apicancelled":
      return isDarkMode ? "bg-red-900/50 text-red-300" : "bg-red-100 text-red-700";
    default:
      return isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600";
  }
}

/**
 * Column group definition for shading
 */
export interface ColumnGroup {
  id: string;
  columns: string[];
}

/**
 * Background colors for column groups in data cells (dark mode)
 * Simple alternating pattern: shaded / unshaded
 */
const CELL_GROUP_COLORS_DARK: Record<string, string> = {
  "symbol-info": "bg-gray-800/50",
  "current-position": "bg-gray-750/50",
  "target-position": "bg-gray-800/50",
  "trade-action": "bg-gray-750/50",
  "stage-assignment": "bg-gray-800/50",
  execution: "bg-gray-750/50",
};

/**
 * Background colors for column groups in data cells (light mode)
 * Simple alternating pattern: shaded / unshaded
 */
const CELL_GROUP_COLORS_LIGHT: Record<string, string> = {
  "symbol-info": "bg-gray-50",
  "current-position": "bg-white",
  "target-position": "bg-gray-50",
  "trade-action": "bg-white",
  "stage-assignment": "bg-gray-50",
  execution: "bg-white",
};

export interface TableRowProps {
  row: PortfolioRow;
  columns: ColumnDefinition[];
  isDarkMode: boolean;
  /** When true, adds left padding for grouped display */
  isGrouped?: boolean;
  /** Optional: Cancel order callback (stageId, orderId) */
  onCancelOrder?: (stageId: number, orderId: string) => Promise<void>;
  /** Optional: Refresh order status callback (stageId, orderId) */
  onRefreshOrderStatus?: (stageId: number, orderId: string) => Promise<void>;
  /** Optional: Fetch trades callback (stageId, orderId) */
  onFetchTrades?: (stageId: number, orderId: string) => Promise<void>;
  // === Phase 2: Interactive editing props ===
  /** Available stages for dropdown selection */
  stageOptions?: StageOption[];
  /** Callback when stage assignment changes */
  onStageChange?: (conId: number, stageId: number | null) => void;
  /** Callback when trade qty is edited */
  onTradeQtyChange?: (conId: number, newQty: number) => void;
  /** Whether this row has unsaved local changes */
  isDirty?: boolean;
  /** Column groups for cell shading */
  columnGroups?: ColumnGroup[];
}

/**
 * Get cell-specific styling based on column type and value
 */
function getCellClassName(column: ColumnDefinition, row: PortfolioRow, isDarkMode: boolean): string {
  const baseClasses = `px-2 py-1 font-mono border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`;

  // Alignment
  const alignClass = column.align === "right" ? "text-right" : column.align === "center" ? "text-center" : "text-left";

  // Diff column background (removed - using neutral styling)
  const diffBg = "";

  // Special coloring for specific columns
  let colorClass = isDarkMode ? "text-gray-300" : "text-gray-700";

  switch (column.id) {
    case "symbol":
      colorClass = `font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`;
      break;
    case "shares":
    case "qty": {
      const posColor = getPositionColorClass(row.shares);
      if (posColor) colorClass = posColor;
      break;
    }
    case "realizedPnl":
    case "unrealizedPnl":
    case "unrealizedPnlUsd": {
      const pnlColor = getPnlColorClass(column.accessor(row) as number);
      if (pnlColor) colorClass = pnlColor;
      break;
    }
    case "tradeQty":
    case "tradeValue":
      const val = column.accessor(row) as number | null;
      if (val !== null) {
        colorClass = val > 0 ? "text-green-500" : val < 0 ? "text-red-500" : colorClass;
      }
      break;
    case "conId":
      colorClass = isDarkMode ? "text-gray-500" : "text-gray-400";
      break;
    case "group":
    case "sector":
      colorClass = isDarkMode ? "text-gray-400" : "text-gray-500";
      break;
    case "fxRate":
      const fxVal = row.fxRate;
      if (fxVal !== null && fxVal !== 1) {
        colorClass = isDarkMode ? "text-amber-400" : "text-amber-600";
      } else {
        colorClass = isDarkMode ? "text-gray-500" : "text-gray-400";
      }
      break;
  }

  return `${baseClasses} ${alignClass} ${diffBg} ${colorClass}`;
}

/**
 * Props for renderCellContent to support order actions and editing
 */
interface RenderCellContentOptions {
  column: ColumnDefinition;
  row: PortfolioRow;
  isDarkMode: boolean;
  onCancelOrder?: (stageId: number, orderId: string) => Promise<void>;
  onRefreshOrderStatus?: (stageId: number, orderId: string) => Promise<void>;
  onFetchTrades?: (stageId: number, orderId: string) => Promise<void>;
  isCancelling?: boolean;
  setIsCancelling?: (value: boolean) => void;
  isRefreshing?: boolean;
  setIsRefreshing?: (value: boolean) => void;
  isFetchingTrades?: boolean;
  setIsFetchingTrades?: (value: boolean) => void;
  // Phase 2: Interactive editing
  stageOptions?: StageOption[];
  onStageChange?: (conId: number, stageId: number | null) => void;
  isLocked?: boolean;
  isHold?: boolean;
  // Trade Qty editing state
  isEditingQty?: boolean;
  editingQtyValue?: string;
  setEditingQtyValue?: (value: string) => void;
  onStartEditQty?: () => void;
  onCommitQty?: () => void;
  onCancelEditQty?: () => void;
}

/**
 * Render cell content with special handling for certain column types
 */
function renderCellContent({
  column,
  row,
  isDarkMode,
  onCancelOrder,
  onRefreshOrderStatus,
  onFetchTrades,
  isCancelling,
  setIsCancelling,
  isRefreshing,
  setIsRefreshing,
  isFetchingTrades,
  setIsFetchingTrades,
  // Phase 2: Interactive editing
  stageOptions,
  onStageChange,
  isLocked,
  isHold,
  isEditingQty,
  editingQtyValue,
  setEditingQtyValue,
  onStartEditQty,
  onCommitQty,
  onCancelEditQty,
}: RenderCellContentOptions): ReactNode {
  const rawValue = column.accessor(row);

  // Handle null USD values as pending (FX rates still loading)
  if (isUsdColumn(column.id) && isUsdValueUnavailable(rawValue)) {
    return <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>{USD_NULL_DISPLAY}</span>;
  }

  // Special handling for tradeAction column
  if (column.id === "tradeAction") {
    return <TradeActionBadge action={row.tradeAction} />;
  }

  // Special handling for secType - render as plain text
  if (column.id === "secType") {
    const value = rawValue as string | null;
    return value || "-";
  }

  // Special handling for symbol - append currency badge
  if (column.id === "symbol") {
    const symbol = rawValue as string | null;
    const currency = row.currency;
    // Only show badge for non-USD currencies
    const showBadge = currency && currency !== "USD";
    return (
      <span className="flex items-center gap-1">
        <span>{symbol || "-"}</span>
        {showBadge && (
          <span
            className={`text-[9px] px-1 py-0 rounded font-medium ${
              isDarkMode
                ? "bg-gray-700 text-gray-400"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            {currency}
          </span>
        )}
      </span>
    );
  }

  // === Phase 2.1: Stage column as dropdown ===
  if (column.id === "stage") {
    // Excluded rows: show backend stage (source of truth) as read-only, or "Unassigned" if none
    if (row.isExcluded) {
      const reason = row.exclusionReasons?.includes("zero_price")
        ? "No valid price -- row excluded from staging"
        : row.exclusionReasons?.includes("invalid_exchange")
        ? "Invalid exchange (VALUE) -- row excluded from staging"
        : row.exclusionReasons?.includes("retirement_short")
        ? "Retirement account -- short positions excluded from staging"
        : "Excluded from trading";
      const displayValue = row.stageName || (row.stageId ? `Stage ${row.stageId}` : "Unassigned");
      return (
        <span
          className={isDarkMode ? "text-gray-600 italic" : "text-gray-400 italic"}
          title={reason}
        >
          {displayValue}
        </span>
      );
    }

    // HOLD trades (tradeQty = 0) that have NO stage assignment show "-"
    // But if a stage was already assigned (e.g., completed stages), show it
    if (isHold && !row.stageId) {
      return <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>-</span>;
    }

    // If no stage options or no callback, fall back to default rendering
    if (!stageOptions || !onStageChange) {
      return row.stageName || (row.stageId ? String(row.stageId) : "-");
    }

    // Phase 2: Check if stage dropdown is locked based on stage state
    const stageLocked = isStageDropdownLocked(row);
    const lockReason = isLocked
      ? "Cannot change - order in progress"
      : row.stageState && LOCKED_STAGE_STATES.includes(row.stageState)
      ? `Cannot change - stage is ${row.stageState}. Reset to Created via Stage Manager.`
      : undefined;

    // Check if the row's current stage is already in stageOptions
    // If not (e.g., stage is ready/executing/completed), we add it so the dropdown displays correctly
    const currentStageInOptions = stageOptions?.some(s => s.id === row.stageId) ?? false;

    // Stage dropdown
    return (
      <select
        value={row.stageId ?? ""}
        onChange={e => {
          const value = e.target.value;
          onStageChange(row.conId, value === "" ? null : Number(value));
        }}
        disabled={stageLocked}
        className={`w-full px-1 py-0.5 text-xs rounded border ${
          isDarkMode ? "bg-gray-700 border-gray-600 text-gray-200" : "bg-white border-gray-300 text-gray-700"
        } ${stageLocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        title={lockReason}
      >
        <option value="">Unassigned</option>
        {/* Include currently assigned stage if not in stageOptions (e.g., ready/executing/completed) */}
        {row.stageId && !currentStageInOptions && (
          <option value={row.stageId}>{row.stageName || `Stage ${row.stageId}`}</option>
        )}
        {stageOptions.map(stage => (
          <option key={stage.id} value={stage.id}>
            {stage.name}
          </option>
        ))}
      </select>
    );
  }

  // === Phase 2.2: Trade Qty with double-click to edit ===
  if (column.id === "tradeQty" && onCommitQty) {
    if (isEditingQty && setEditingQtyValue && onCancelEditQty) {
      // Editing mode
      return (
        <input
          type="number"
          value={editingQtyValue}
          onChange={e => setEditingQtyValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              onCommitQty();
            } else if (e.key === "Escape") {
              onCancelEditQty();
            }
          }}
          onBlur={onCommitQty}
          autoFocus
          className={`w-16 px-1 py-0.5 text-xs text-right rounded border ${
            isDarkMode ? "bg-gray-700 border-blue-500 text-white" : "bg-white border-blue-500 text-gray-900"
          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
        />
      );
    }

    // Display mode with double-click to edit
    const canEdit = !isLocked && onStartEditQty;
    return (
      <span
        onDoubleClick={canEdit ? onStartEditQty : undefined}
        className={`font-mono px-1 py-0.5 rounded ${
          canEdit
            ? `cursor-pointer border border-dashed ${
                isDarkMode ? "border-blue-600/50 hover:bg-blue-900/30" : "border-blue-400/50 hover:bg-blue-50"
              }`
            : ""
        } ${isLocked ? "cursor-not-allowed" : ""}`}
        title={isLocked ? "Cannot edit - order in progress" : canEdit ? "Double-click to edit" : undefined}
      >
        {column.formatter ? column.formatter(rawValue, row) : rawValue ?? "-"}
      </span>
    );
  }

  // === Phase 2.3: Drift column with icon ===
  if (column.id === "drift") {
    // Not saved yet - show dash
    if (row.stageQty === null) {
      return <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>-</span>;
    }

    // Drift detected - warning icon
    if (row.hasDrift) {
      return (
        <span
          className={`inline-flex items-center justify-center w-6 h-6 rounded ${
            isDarkMode ? "bg-orange-900/50 text-orange-400" : "bg-orange-100 text-orange-600"
          }`}
          title={`Drift: Stage Qty (${row.stageQty}) differs from Trade Qty (${row.tradeQty})`}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
        </span>
      );
    }

    // No drift - checkmark
    return (
      <span
        className={`inline-flex items-center justify-center ${isDarkMode ? "text-green-400" : "text-green-600"}`}
        title="Stage Qty matches Trade Qty"
      >
        <Check className="w-4 h-4" />
      </span>
    );
  }

  // === Threshold Status (TH) column with color-coded badges ===
  if (column.id === "thresholdStatus") {
    const value = row.thresholdStatus;
    
    // "Y" = Excluded - trade below threshold, left unassigned (amber/warning)
    if (value === "Y") {
      return (
        <span 
          className={`px-1.5 py-0.5 text-xs font-medium rounded ${
            isDarkMode ? "bg-amber-900/50 text-amber-300" : "bg-amber-100 text-amber-700"
          }`}
          title="Excluded - trade below noise threshold, left unassigned"
        >
          Y
        </span>
      );
    }
    
    // "E" = Exempt - trade below threshold but included (position close) (blue/info)
    if (value === "E") {
      return (
        <span 
          className={`px-1.5 py-0.5 text-xs font-medium rounded ${
            isDarkMode ? "bg-blue-900/50 text-blue-300" : "bg-blue-100 text-blue-700"
          }`}
          title="Exempt - position close, included regardless of size"
        >
          E
        </span>
      );
    }
    
    // Blank/normal - no styling
    return <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>-</span>;
  }

  // Special handling for orderStatus - render as badge with action buttons
  if (column.id === "orderStatus") {
    const status = rawValue as string | null;
    if (!status) return "-";

    const statusLower = status.toLowerCase();
    const isPending = statusLower === "submitted" || statusLower === "presubmitted" || statusLower === "partialfill";
    const isFilled = statusLower === "filled" || statusLower === "partialfill";
    const statusColor = getOrderStatusColor(status, isDarkMode);
    const hasOrder = row.stageId && row.orderId;
    const canCancel = isPending && hasOrder && onCancelOrder;
    const canRefresh = hasOrder && onRefreshOrderStatus;
    const canFetchTrades = isFilled && hasOrder && onFetchTrades;

    const handleCancel = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!row.stageId || !row.orderId || !onCancelOrder || !setIsCancelling) return;
      setIsCancelling(true);
      try {
        await onCancelOrder(row.stageId, row.orderId);
      } catch (err) {
        console.error("Failed to cancel order:", err);
      } finally {
        setIsCancelling(false);
      }
    };

    const handleRefresh = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!row.stageId || !row.orderId || !onRefreshOrderStatus || !setIsRefreshing) return;
      setIsRefreshing(true);
      try {
        await onRefreshOrderStatus(row.stageId, row.orderId);
      } catch (err) {
        console.error("Failed to refresh order status:", err);
      } finally {
        setIsRefreshing(false);
      }
    };

    const handleFetchTrades = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!row.stageId || !row.orderId || !onFetchTrades || !setIsFetchingTrades) return;
      setIsFetchingTrades(true);
      try {
        await onFetchTrades(row.stageId, row.orderId);
      } catch (err) {
        console.error("Failed to fetch trades:", err);
      } finally {
        setIsFetchingTrades(false);
      }
    };

    const isAnyLoading = isCancelling || isRefreshing || isFetchingTrades;

    return (
      <div className="flex items-center gap-1">
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusColor}`}>{status}</span>
        {/* Refresh button - always available when order exists */}
        {canRefresh && (
          <button
            onClick={handleRefresh}
            disabled={isAnyLoading}
            className={`p-0.5 rounded transition-colors ${
              isAnyLoading
                ? "opacity-50 cursor-not-allowed"
                : isDarkMode
                ? "hover:bg-blue-900/30 text-blue-400"
                : "hover:bg-blue-100 text-blue-500"
            }`}
            title="Refresh order status from broker"
          >
            {isRefreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        )}
        {/* Fetch trades button - for filled orders */}
        {canFetchTrades && (
          <button
            onClick={handleFetchTrades}
            disabled={isAnyLoading}
            className={`p-0.5 rounded transition-colors ${
              isAnyLoading
                ? "opacity-50 cursor-not-allowed"
                : isDarkMode
                ? "hover:bg-green-900/30 text-green-400"
                : "hover:bg-green-100 text-green-500"
            }`}
            title="Fetch trade details from broker"
          >
            {isFetchingTrades ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
          </button>
        )}
        {/* Cancel button - for pending orders */}
        {canCancel && (
          <button
            onClick={handleCancel}
            disabled={isAnyLoading}
            className={`p-0.5 rounded transition-colors ${
              isAnyLoading
                ? "opacity-50 cursor-not-allowed"
                : isDarkMode
                ? "hover:bg-red-900/30 text-red-400"
                : "hover:bg-red-100 text-red-500"
            }`}
            title="Cancel order"
          >
            {isCancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
          </button>
        )}
      </div>
    );
  }

  // Use formatter if available
  if (column.formatter) {
    return column.formatter(rawValue, row);
  }

  // Default: return raw value or dash for null/undefined
  return rawValue ?? "-";
}

const TableRow: React.FC<TableRowProps> = ({
  row,
  columns,
  isDarkMode,
  isGrouped = false,
  onCancelOrder,
  onRefreshOrderStatus,
  onFetchTrades,
  // Phase 2: Interactive editing props
  stageOptions,
  onStageChange,
  onTradeQtyChange,
  isDirty = false,
  columnGroups,
}) => {
  // Helper to get column group background color
  // Disabled: Keep pure black background in dark mode for cleaner look
  const getColumnGroupBg = (_columnId: string): string => {
    return "";
  };
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFetchingTrades, setIsFetchingTrades] = useState(false);

  // Phase 2.2: Trade Qty editing state
  const [isEditingQty, setIsEditingQty] = useState(false);
  const [editingQtyValue, setEditingQtyValue] = useState("");

  // Compute row states
  const isLocked = isStageDropdownLocked(row);
  const isHold = isHoldTrade(row);
  const hasDrift = row.hasDrift && row.stageQty !== null;

  // Trade Qty editing handlers
  const handleStartEditQty = useCallback(() => {
    if (isLocked || row.isExcluded) return;
    setEditingQtyValue(String(row.tradeQty ?? 0));
    setIsEditingQty(true);
  }, [isLocked, row.isExcluded, row.tradeQty]);

  const handleCommitQty = useCallback(() => {
    if (!isEditingQty) return;
    const parsed = parseFloat(editingQtyValue);
    if (!isNaN(parsed) && onTradeQtyChange) {
      // Round to 4 decimal places to avoid floating point issues
      const newQty = Math.round(parsed * 10000) / 10000;
      onTradeQtyChange(row.conId, newQty);
    }
    setIsEditingQty(false);
    setEditingQtyValue("");
  }, [isEditingQty, editingQtyValue, row.conId, onTradeQtyChange]);

  const handleCancelEditQty = useCallback(() => {
    setIsEditingQty(false);
    setEditingQtyValue("");
  }, []);

  // === Phase 2.3: Row state classes ===
  // Locked rows: no background change (was grayed out)
  const lockedClass = "";

  // Dirty rows: blue left border
  const dirtyClass = isDirty ? "border-l-4 border-l-blue-500" : "";

  // Drift rows: subtle orange/yellow highlight
  const driftClass = hasDrift && !isLocked ? (isDarkMode ? "bg-orange-900/10" : "bg-orange-50/50") : "";

  // Excluded rows: data-quality issues (zero-price, invalid exchange) get grey dimmed, manual gets amber highlight
  // If both reasons apply, data-quality style takes precedence (more severe)
  const isDataQualityExclusion = row.exclusionReasons?.includes("zero_price")
    || row.exclusionReasons?.includes("invalid_exchange")
    || row.exclusionReasons?.includes("retirement_short");

  const excludedClass = row.isExcluded
    ? isDataQualityExclusion
      ? (isDarkMode ? "opacity-50 bg-gray-800/40" : "opacity-60 bg-gray-100")
      : (isDarkMode ? "bg-amber-900/30" : "bg-amber-100")
    : "";

  return (
    <tr
      className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} ${
        isDarkMode ? "hover:bg-gray-750" : "hover:bg-gray-50"
      } transition-colors ${
        row.isSynthetic ? "opacity-75" : ""
      } ${lockedClass} ${dirtyClass} ${driftClass} ${excludedClass}`}
    >
      {columns.map((column, index) => {
        const isFirstColumn = index === 0;
        const cellClassName = getCellClassName(column, row, isDarkMode);
        const extraPadding = isFirstColumn && isGrouped ? "pl-8" : "";
        const groupBg = getColumnGroupBg(column.id);
        
        // Sticky first column (symbol)
        const stickyClass = isFirstColumn 
          ? `sticky left-0 z-10 ${isDarkMode ? "bg-black" : "bg-white"}` 
          : "";

        return (
          <td
            key={column.id}
            className={`${cellClassName} ${extraPadding} ${groupBg} ${stickyClass}`}
            style={column.width ? { width: column.width, minWidth: column.width } : undefined}
            title={
              column.id === "symbol" || column.id === "group" || column.id === "sector"
                ? String(column.accessor(row) || "")
                : undefined
            }
          >
            {renderCellContent({
              column,
              row,
              isDarkMode,
              onCancelOrder,
              onRefreshOrderStatus,
              onFetchTrades,
              isCancelling,
              setIsCancelling,
              isRefreshing,
              setIsRefreshing,
              isFetchingTrades,
              setIsFetchingTrades,
              // Phase 2: Interactive editing
              stageOptions,
              onStageChange,
              isLocked,
              isHold,
              isEditingQty: isEditingQty && column.id === "tradeQty",
              editingQtyValue,
              setEditingQtyValue,
              onStartEditQty: handleStartEditQty,
              onCommitQty: handleCommitQty,
              onCancelEditQty: handleCancelEditQty,
            })}
          </td>
        );
      })}
    </tr>
  );
};

export default TableRow;
